import { Copy, ShoppingBag, Trash } from 'react-feather';
import { DocumentNode } from 'graphql';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetProductQuery as GetProduct,
  GetProductsQuery as GetProducts,
  ProductType,
  PurchaseStatus,
} from '__generated__/graphql';

import { Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl, mergeUrlParams } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import { useSellableStatus } from '../../../Common/SellableStatusIndicator';
import ActivateProductMutation from './ActivateProductMutation';
import CardPreview from '../CardPreview';
import DeactivateProductMutation from './DeactivateProductMutation';
import DeleteProductMutation from './DeleteProductMutation';
import DuplicateProductMutation from './DuplicateProductMutation';
import EditProductForm from './EditProductForm';
import GetProductsQuery from './GetProductsQuery';
import PriceRange from '../../../../common/Common/PriceRange';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';

 type Event = GetProducts['project']['event'];
 type SimpleProduct = Event['products'][0];

export interface ProductListProps {
  event: Event;
  products: SimpleProduct[];
  productType: 'ticket_upgrade' | 'stand_alone_product';
  activeProductId?: string;
  productData?: GetProduct;
  openAddProductModal: () => void;
  refetchQueries?: DocumentNode[];
}

const ProductList = ({
  event, products, productType, activeProductId, productData, openAddProductModal,
  refetchQueries = [GetProductsQuery],
}: ProductListProps) => {
  const { t } = useTranslation();
  const project = useProject();

  return (
    <>
      <UI.HR />
      <UI.Div>
        <UI.H3>
          <UI.Icon sc={{ mr: 1.5 }}>
            {productType === 'ticket_upgrade' ? <UI.Icons.Ticket /> : <ShoppingBag />}
          </UI.Icon>
          {t(productType === 'ticket_upgrade' ? 'ticket_upgrades' : 'stand_alone_products')}
        </UI.H3>
      </UI.Div>
      <UI.GridContainer sc={{ gutter: 1 / 3 }}>
        {products.map((product) => (
          <UI.CollapsibleNavCard
            to={`/${project.id}/events/${event.id}/products/${activeProductId !== product.id ? product.id : ''}`}
            open={product.id === activeProductId}
            muted={!!activeProductId && product.id !== activeProductId}
            preview={(
              <ProductPreview
                product={product}
                event={event}
                refetchQueries={refetchQueries}
              />
        )}
            body={productData
              ? <EditProductForm product={productData.product} event={productData.event} />
              : <UI.PageLoader />}
            id={`ProductCard_${product.id}`}
            key={product.id}
          />
        ))}
        {products.length === 0 && (
          <UI.Card sc={{ background: 'gray.50', textAlign: 'center' }}>
            <UI.Link
              to={mergeUrlParams({ product_type: productType })}
              onClick={() => openAddProductModal()}
            >
              {t(productType === 'ticket_upgrade' ? 'add_a_ticket_upgrade' : 'add_a_stand_alone_product')}
            </UI.Link>
          </UI.Card>
        )}
      </UI.GridContainer>
    </>
  );
};

interface ProductPreviewProps {
  product: SimpleProduct;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const ProductPreview = ({ product, event, refetchQueries = [] }: ProductPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const history = useHistory();

  const [activateProduct] = useMutation(ActivateProductMutation, {
    variables: {
      id: product.id,
    },
    optimisticResponse: {
      activateProduct: {
        id: product.id,
        active: true,
        __typename: 'Product',
      },
    },
    onCompleted: () => notifier.success(t('notifications.product_activated')),
  });

  const [deactivateProduct] = useMutation(DeactivateProductMutation, {
    variables: {
      id: product.id,
    },
    optimisticResponse: {
      deactivateProduct: {
        id: product.id,
        active: false,
        __typename: 'Product',
      },
    },
    onCompleted: () => notifier.success(t('notifications.product_deactivated')),
  });

  const toggleStatus = () => {
    if (product.active) {
      deactivateProduct();
    } else {
      activateProduct();
    }
  };

  const [duplicateProduct, { loading: duplicating }] = useMutation(DuplicateProductMutation, {
    variables: {
      id: product.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.product_duplicated'));

      history.push(`/${project.id}/events/${event.id}/products`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [deleteProduct, { loading: deleting }] = useMutation(DeleteProductMutation, {
    variables: {
      id: product.id,
    },
    onCompleted: () => {
      notifier.success(t('notifications.product_deleted'));

      history.push(`/${project.id}/events/${event.id}/products`);
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
    refetchQueries,
    awaitRefetchQueries: refetchQueries.length > 0,
  });

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: product.title }),
    onConfirm: () => deleteProduct(),
  }, [product]);

  const { message } = useSellableStatus(product);

  return (
    <CardPreview
      title={product.title}
      firstColumn={(
        <UI.Span sc={{ color: 'gray.500' }}>
          <UI.Delimit>
            {[ProductType.item, ProductType.fee].includes(product.type) && (
            <PriceRange prices={product.promotions.map(({ amount }) => ({ amount }))} />
            )}
            {product.type === ProductType.donation && t('donation')}
            {product.type === ProductType.fee && t('fee')}
          </UI.Delimit>
        </UI.Span>
      )}
      secondColumn={(
        <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
          <UI.Link
            to={product.stand_alone
              ? `/${project.id}/participants/orders?${encodeUrl({
                event: event.id,
                products: [product.id],
              })}`
              : `/${project.id}/participants?${encodeUrl({
                event: event.id,
                products: [product.id],
                statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
              })}`}
            sc={{ secondary: true }}
          >
            <UI.Icon>
              <ShoppingBag />
            </UI.Icon>
            {' '}
            {` ${formatInteger(product.sold_count)} / ${product.capacity !== null ? formatInteger(product.capacity) : '∞'}`}
          </UI.Link>
          <UI.Tooltip sc={{ alignH: 'center' }}>
            {t('purchases')}
          </UI.Tooltip>
        </UI.TooltipContainer>
      )}
      status={message}
      active={product.active}
      onToggle={toggleStatus}
      options={() => (
        <>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => duplicateProduct()}
            disabled={duplicating}
          />
          {product.deletable && (
            <Option
              icon={<Trash />}
              label={t('delete')}
              onClick={() => confirmDelete()}
              disabled={deleting}
            />
          )}
        </>
      )}
    />
  );
};

export default ProductList;
